import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isTomorrow, addDays } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  Plus, 
  ChevronRight, 
  Bell,
  CheckCircle2,
  ListTodo,
  Repeat,
  TrendingUp,
  ArrowRight,
  Dumbbell,
  ShoppingCart,
  ChevronLeft,
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Header } from '@/components/Header';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { tasks } = useApp();

  // Get upcoming tasks (next 7 days)
  const upcomingTasks = tasks
    .filter(task => {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      const sevenDaysFromNow = addDays(new Date(), 7);
      return dueDate <= sevenDaysFromNow && !task.completed;
    })
    .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime());

  // Calculate stats
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.completed).length;
  const completionRate = totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const recurringTasks = tasks.filter(t => t.isRecurring).length;

  // Category distribution
  const categoryCount = tasks.reduce((acc, task) => {
    const category = task.category.toLowerCase();
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const categoryColors = {
    personal: 'bg-ai-tasky-personal',
    work: 'bg-ai-tasky-work',
    health: 'bg-ai-tasky-health',
    learning: 'bg-ai-tasky-learning',
  };

  const getCategoryStyles = (category: string) => {
    switch (category.toLowerCase()) {
      case 'learning':
        return 'bg-gradient-to-br from-amber-500/90 to-orange-400/90 border-orange-300 text-white';
      case 'personal':
        return 'bg-gradient-to-br from-violet-500/90 to-purple-400/90 border-purple-300 text-white';
      case 'work':
        return 'bg-gradient-to-br from-sky-500/90 to-blue-400/90 border-blue-300 text-white';
      case 'health':
        return 'bg-gradient-to-br from-emerald-500/90 to-green-400/90 border-green-300 text-white';
      case 'shopping':
        return 'bg-gradient-to-br from-teal-500/90 to-cyan-400/90 border-cyan-300 text-white';
      case 'gym':
        return 'bg-gradient-to-br from-rose-500/90 to-pink-400/90 border-pink-300 text-white';
      default:
        return 'bg-gradient-to-br from-gray-500/90 to-slate-400/90 border-slate-300 text-white';
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header 
        title="Dashboard"
        showWelcome={true}
        subtitle="Here's an overview of your tasks"
      />
      
      <div className="flex-1 p-4 md:p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats Overview */}
        <Card className="bg-white/50 backdrop-blur-lg">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <ListTodo className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Tasks</p>
                  <p className="text-xl font-bold">{totalTasks}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Completed</p>
                  <p className="text-xl font-bold">{completedTasks}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                  <p className="text-xl font-bold">{completionRate}%</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Repeat className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Recurring</p>
                  <p className="text-xl font-bold">{recurringTasks}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-white/50 backdrop-blur-lg">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Button 
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/tasks')}
              >
                <ListTodo className="w-6 h-6" />
                <span>View All Tasks</span>
              </Button>
              <Button 
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-pink-500 hover:bg-pink-600 text-white transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/calendar')}
              >
                <Calendar className="w-6 h-6" />
                <span>Calendar View</span>
              </Button>
              <Button 
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/create')}
              >
                <Plus className="w-6 h-6" />
                <span>New Task</span>
              </Button>
              <Button 
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/reminders')}
              >
                <Bell className="w-6 h-6" />
                <span>Reminders</span>
              </Button>
              <Button 
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white transition-all duration-300 hover:scale-105"
                onClick={() => {
                  navigate('/create', { 
                    state: { 
                      category: 'Health',
                      title: 'Gym Workout',
                      description: 'Time to exercise!'
                    }
                  });
                }}
              >
                <Dumbbell className="w-6 h-6" />
                <span>Gym</span>
              </Button>
              <Button 
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white transition-all duration-300 hover:scale-105"
                onClick={() => {
                  navigate('/create', { 
                    state: { 
                      category: 'Shopping',
                      title: 'Grocery Shopping',
                      description: 'Buy groceries'
                    }
                  });
                }}
              >
                <ShoppingCart className="w-6 h-6" />
                <span>Grocery</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Tasks Carousel */}
        <Card className="bg-white/80 backdrop-blur-md border border-white/20 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="font-extrabold text-xl">Upcoming Tasks</CardTitle>
              <p className="text-sm text-gray-500 font-medium">Swipe to see more tasks</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/calendar')}
              className="font-bold hover:bg-gray-100"
            >
              View Calendar
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingTasks.length > 0 ? (
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: true,
                }}
                className="w-full relative group"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {upcomingTasks.map((task) => {
                    const dueDate = new Date(task.dueDate!);
                    let dueDateText = format(dueDate, 'MMM d');
                    if (isToday(dueDate)) dueDateText = 'Today';
                    if (isTomorrow(dueDate)) dueDateText = 'Tomorrow';

                    return (
                      <CarouselItem 
                        key={task.id} 
                        className="pl-2 md:pl-4 basis-[85%] md:basis-1/2 lg:basis-1/3"
                      >
                        <div
                          className={cn(
                            "group cursor-pointer rounded-xl p-3 border transition-all duration-300 h-full",
                            "hover:shadow-lg hover:scale-[1.02] backdrop-blur-sm",
                            "relative overflow-hidden",
                            getCategoryStyles(task.category)
                          )}
                          onClick={() => navigate(`/task/${task.id}`)}
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-black/[0.05] to-black/[0.15] pointer-events-none" />
                          <div className="space-y-1.5 relative">
                            <div className="flex items-start justify-between">
                              <h3 className="font-extrabold truncate text-base">
                                {task.title}
                              </h3>
                              {task.isRecurring && (
                                <Badge variant="outline" className="text-[10px] border-white/40 bg-white/10 text-white backdrop-blur-sm font-extrabold px-1 py-0 ml-1.5">
                                  ↻
                                </Badge>
                              )}
                            </div>

                            {task.description && (
                              <p className="text-xs font-bold line-clamp-2 text-white/90">
                                {task.description}
                              </p>
                            )}

                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge className="font-extrabold capitalize text-[10px] px-1.5 py-0 bg-white/20 text-white border-white/10">
                                {task.category}
                              </Badge>
                              <Badge variant="outline" className="font-extrabold text-[10px] px-1.5 py-0 border-white/20 text-white">
                                {dueDateText}
                              </Badge>
                              <Badge variant="outline" className="font-extrabold text-[10px] px-1.5 py-0 border-white/20 text-white">
                                {format(dueDate, 'h:mm a')}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
                <div className="absolute -bottom-4 left-0 right-0 flex justify-center gap-1 pb-4">
                  {upcomingTasks.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "w-1.5 h-1.5 rounded-full transition-all duration-300",
                        "bg-gray-300"
                      )}
                    />
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 hidden md:flex h-8 w-8 rounded-full bg-white/80 hover:bg-white shadow-md"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:flex h-8 w-8 rounded-full bg-white/80 hover:bg-white shadow-md"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Carousel>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 font-bold">
                  No upcoming tasks
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Home;
